<template>
    <div class="tab-content">
        <div class="file-upload-container">
            <div
                class="drop-zone"
                :class="{ highlight: isHighlighted }"
                @dragover.prevent="highlightDropzone"
                @dragleave="unhighlightDropzone"
                @drop="handleDrop"
            >
                <div v-if="imagePreview" class="image-preview">
                    <img :src="imagePreview" alt="Preview" />
                    <button class="remove-preview" @click="removePreview">×</button>
                </div>
                <div v-else id="dropZoneContent">
                    <p>Перетащите изображение комнаты сюда или</p>
                    <button class="btn secondary" @click="triggerFileInput">Выберите файл</button>
                    <p>Максимальный размер: 5MB</p>
                </div>
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/jpeg,image/png"
                    style="display: none"
                    @change="handleFileSelect"
                />
            </div>

            <div class="or-separator">
                <span>ИЛИ</span>
            </div>

            <div class="url-input-group">
                <input
                    v-model="imageUrl"
                    type="text"
                    placeholder="https://example.com/room.jpg"
                    @input="handleUrlInput"
                />
            </div>
        </div>

        <div class="controls">
            <div class="input-group">
                <label for="furnitureLimit">Лимит рекомендаций</label>
                <input
                    id="furnitureLimit"
                    v-model.number="furnitureLimit"
                    type="number"
                    min="1"
                    max="10"
                    placeholder="5"
                />
            </div>

            <div class="input-group">
                <label for="minConfidence">Минимальная уверенность</label>
                <input
                    id="minConfidence"
                    v-model.number="minConfidence"
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    placeholder="0.5"
                />
            </div>

            <button class="btn" :disabled="!processEnabled || processing" @click="processImage">
                <span v-if="processing">Обработка...</span>
                <span v-else>Распознать мебель</span>
            </button>
        </div>

        <div class="preview-area">
            <div class="image-preview-card">
                <div class="image-preview-header">Предпросмотр изображения</div>
                <div class="image-preview-content">
                    <img v-if="sidebarImage" :src="sidebarImage" alt="Image preview" />
                    <div v-else style="color: var(--text-light); text-align: center">
                        Изображение не загружено
                    </div>
                </div>
            </div>

            <div id="result" class="results-container">
                <div v-if="processing" class="loading">
                    <div class="spinner"></div>
                    <p>Обработка изображения...</p>
                </div>

                <div v-else-if="errorMessage" class="error">
                    {{ errorMessage }}
                </div>

                <template v-else-if="results">
                    <div v-if="results.room_analysis" class="room-analysis">
                        <h2>Анализ комнаты: {{ results.room_analysis.room_type }}</h2>
                        <div class="reasoning">
                            <strong>Анализ:</strong> {{ results.room_analysis.reasoning }}
                        </div>
                    </div>

                    <div
                        v-if="results.detected_furniture && results.detected_furniture.length"
                        class="detected-furniture-section"
                    >
                        <h2>Обнаруженная мебель</h2>
                        <div class="detected-furniture">
                            <div
                                v-for="(item, index) in results.detected_furniture"
                                :key="index"
                                class="furniture-card"
                            >
                                <div class="furniture-header">
                                    <h3 class="furniture-title">{{ item.type }}</h3>
                                    <div
                                        class="confidence"
                                        :style="{ background: getConfidenceColor(item.confidence) }"
                                    >
                                        {{ Math.round(item.confidence * 100) }}%
                                    </div>
                                </div>
                                <div class="furniture-attributes">
                                    <div
                                        v-for="(value, key) in item.attributes"
                                        :key="key"
                                        class="attribute-group"
                                    >
                                        <div class="attribute-label">{{ key }}:</div>
                                        <div class="attribute-value">
                                            <template v-if="Array.isArray(value)">
                                                {{ value.join(', ') }}
                                            </template>
                                            <template v-else-if="typeof value === 'object'">
                                                <pre class="json-attribute">{{
                                                    JSON.stringify(value, null, 2)
                                                }}</pre>
                                            </template>
                                            <template v-else>
                                                {{ value }}
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="results.recommendations && results.recommendations.length"
                        class="recommendations"
                    >
                        <h2>Рекомендуемая мебель</h2>
                        <div
                            v-for="(recGroup, groupIndex) in results.recommendations"
                            :key="groupIndex"
                            class="recommendation-group"
                        >
                            <div class="recommendation-title">
                                {{ recGroup.detected_type }} ({{ recGroup.matches.length }}
                                рекомендаций)
                            </div>
                            <div class="recommended-items">
                                <div
                                    v-for="(match, matchIndex) in recGroup.matches"
                                    :key="matchIndex"
                                    class="recommended-item"
                                >
                                    <div class="item-header">
                                        <h4 class="furniture-title">{{ match.type }}</h4>
                                        <div class="similarity-score">
                                            {{ Math.round(match.similarity_score * 100) }}% схожести
                                        </div>
                                    </div>
                                    <div class="item-previews">
                                        <img
                                            v-for="(previewUrl, previewType) in match.preview"
                                            :key="previewType"
                                            :src="previewUrl"
                                            :alt="previewType + ' view'"
                                            :title="previewType"
                                        />
                                        <p
                                            v-if="
                                                !match.preview ||
                                                Object.keys(match.preview).length === 0
                                            "
                                        >
                                            Предпросмотр недоступен
                                        </p>
                                    </div>
                                    <div class="item-attributes">
                                        <div class="attribute-group">
                                            <div class="attribute-label">ID:</div>
                                            <div class="attribute-value">
                                                {{ match.furniture_id }}
                                            </div>
                                        </div>
                                        <div
                                            v-for="(value, key) in match.attributes"
                                            :key="key"
                                            class="attribute-group"
                                        >
                                            <div class="attribute-label">{{ key }}:</div>
                                            <div class="attribute-value">
                                                <template v-if="Array.isArray(value)">
                                                    {{ value.join(', ') }}
                                                </template>
                                                <template v-else-if="typeof value === 'object'">
                                                    <pre class="json-attribute">{{
                                                        JSON.stringify(value, null, 2)
                                                    }}</pre>
                                                </template>
                                                <template v-else>
                                                    {{ value }}
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="results.processing_info" class="processing-info">
                        <h3>Информация о обработке</h3>
                        <p>
                            Обнаружено предметов мебели:
                            {{ results.processing_info.furniture_items_detected }}
                        </p>
                        <p>
                            Размер базы данных мебели:
                            {{ results.processing_info.furniture_database_size }}
                        </p>
                        <p>
                            Минимальная уверенность:
                            {{ results.processing_info.min_confidence_used }}
                        </p>
                    </div>

                    <div
                        v-else-if="
                            !results.detected_furniture || results.detected_furniture.length === 0
                        "
                        class="warning"
                    >
                        <h3>Мебель не обнаружена</h3>
                        <p>На изображении не обнаружено мебели с достаточной уверенностью.</p>
                        <p>
                            Попробуйте использовать другое изображение или уменьшить минимальную
                            уверенность.
                        </p>
                    </div>
                </template>

                <div v-else class="no-results">
                    <p>Загрузите изображение для распознавания мебели</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, computed } from 'vue'

    export default {
        name: 'FurnitureRecognitionTab',
        setup() {
            const fileInput = ref(null)
            const imagePreview = ref(null)
            const imageUrl = ref('')
            const sidebarImage = ref('')
            const furnitureLimit = ref(5)
            const minConfidence = ref(0.5)
            const results = ref(null)
            const isHighlighted = ref(false)
            const processing = ref(false)
            const errorMessage = ref('')

            const processEnabled = computed(() => {
                return imagePreview.value || imageUrl.value.trim()
            })

            function triggerFileInput() {
                if (fileInput.value) {
                    fileInput.value.click()
                }
            }

            function handleFileSelect(event) {
                const file = event.target.files[0]
                if (file) {
                    handleFile(file)
                }
            }

            function handleFile(file) {
                if (file.size > 5 * 1024 * 1024) {
                    alert('Файл слишком большой! Максимальный размер 5MB')
                    return
                }

                const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
                if (!validTypes.includes(file.type)) {
                    alert('Неподдерживаемый формат! Используйте JPG или PNG')
                    return
                }

                imagePreview.value = URL.createObjectURL(file)
                sidebarImage.value = imagePreview.value
                imageUrl.value = ''
            }

            function handleDrop(event) {
                event.preventDefault()
                unhighlightDropzone()

                const files = event.dataTransfer.files
                if (files && files.length > 0) {
                    handleFile(files[0])
                }
            }

            function highlightDropzone() {
                isHighlighted.value = true
            }

            function unhighlightDropzone() {
                isHighlighted.value = false
            }

            function removePreview() {
                imagePreview.value = null
                sidebarImage.value = ''
                if (fileInput.value) {
                    fileInput.value.value = ''
                }
            }

            function handleUrlInput() {
                if (imageUrl.value.trim()) {
                    sidebarImage.value = imageUrl.value
                    imagePreview.value = null
                    if (fileInput.value) {
                        fileInput.value.value = ''
                    }
                } else {
                    sidebarImage.value = ''
                }
            }

            function getConfidenceColor(confidence) {
                const percent = Math.round(confidence * 100)
                if (percent >= 80) return 'var(--success-color)'
                if (percent >= 60) return 'var(--warning-color)'
                return 'var(--error-color)'
            }

            async function processImage() {
                processing.value = true
                errorMessage.value = ''
                results.value = null

                try {
                    const formData = new FormData()

                    if (imagePreview.value) {
                        const blob = await fetch(imagePreview.value).then(r => r.blob())
                        formData.append('image', blob, 'image.jpg')
                    } else if (imageUrl.value) {
                        formData.append('image_url', imageUrl.value)
                    }

                    formData.append('furniture_limit', furnitureLimit.value.toString())
                    formData.append('min_confidence', minConfidence.value.toString())

                    const response = await fetch(
                        'https://suggester.hart-digital.com/process_image_furniture',
                        {
                            method: 'POST',
                            body: formData
                        }
                    )

                    if (!response.ok) {
                        const errorData = await response.json()
                        throw new Error(errorData.error || 'Ошибка сервера')
                    }

                    const data = await response.json()
                    results.value = data
                } catch (error) {
                    console.error('Furniture recognition error:', error)
                    errorMessage.value =
                        'Произошла ошибка при обработке изображения: ' + error.message
                } finally {
                    processing.value = false
                }
            }

            return {
                fileInput,
                imagePreview,
                imageUrl,
                sidebarImage,
                furnitureLimit,
                minConfidence,
                results,
                isHighlighted,
                processing,
                errorMessage,
                processEnabled,
                triggerFileInput,
                handleFileSelect,
                handleDrop,
                highlightDropzone,
                unhighlightDropzone,
                removePreview,
                handleUrlInput,
                getConfidenceColor,
                processImage
            }
        }
    }
</script>

<style scoped>
    .file-upload-container {
        background: #f8faff;
        padding: 20px;
        border-radius: 10px;
        margin-bottom: 30px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .drop-zone {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        border: 2px dashed #d0d9e7;
        border-radius: 8px;
        padding: 30px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-bottom: 20px;
    }

    .drop-zone:hover,
    .drop-zone.highlight {
        border-color: var(--primary-color);
        background: rgba(74, 108, 247, 0.05);
    }

    .or-separator {
        position: relative;
        text-align: center;
        margin: 20px 0;
    }

    .or-separator::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: var(--border-color);
        z-index: 1;
    }

    .or-separator span {
        position: relative;
        z-index: 2;
        background: white;
        padding: 0 15px;
        color: var(--text-light);
    }

    .url-input-group {
        margin-bottom: 20px;
    }

    .url-input-group input {
        width: 100%;
        padding: 12px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
    }

    .controls {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        align-items: flex-end;
        background: #f8faff;
        padding: 20px;
        border-radius: 10px;
        margin-bottom: 30px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .input-group {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 200px;
    }

    .input-group label {
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--text-dark);
    }

    .input-group input {
        padding: 12px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        width: 100%;
        box-sizing: border-box;
    }

    .preview-area {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 25px;
        margin-top: 30px;
    }

    .image-preview-card {
        background: var(--card-bg);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        border: 1px solid #eaeff5;
        height: fit-content;
    }

    .image-preview-header {
        padding: 15px 20px;
        border-bottom: 1px solid #f0f0f0;
        font-weight: 600;
        color: var(--text-dark);
    }

    .image-preview-content {
        padding: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .image-preview-content img {
        max-width: 100%;
        max-height: 400px;
        border-radius: 8px;
    }

    .results-container {
        display: flex;
        flex-direction: column;
        gap: 25px;
    }

    .room-analysis {
        background: var(--card-bg);
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        border: 1px solid #eaeff5;
    }

    .room-analysis h2 {
        margin: 0 0 15px 0;
        color: var(--text-dark);
    }

    .reasoning {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
        border-left: 4px solid var(--primary-color);
    }

    .detected-furniture-section h2,
    .recommendations h2 {
        color: var(--text-dark);
        margin-bottom: 20px;
    }

    .detected-furniture {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .furniture-card {
        background: var(--card-bg);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        border: 1px solid #eaeff5;
    }

    .furniture-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }

    .furniture-header {
        padding: 15px 20px;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .furniture-title {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        color: var(--text-dark);
        text-transform: capitalize;
    }

    .confidence {
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
    }

    .furniture-attributes {
        padding: 20px;
    }

    .attribute-group {
        margin-bottom: 15px;
    }

    .attribute-label {
        font-weight: 600;
        color: var(--text-dark);
        margin-bottom: 5px;
    }

    .attribute-value {
        color: var(--text-light);
        background: #f8f9fa;
        padding: 8px 12px;
        border-radius: 6px;
        border-left: 3px solid var(--primary-color);
    }

    .recommendations {
        margin-top: 30px;
    }

    .recommendation-group {
        margin-bottom: 30px;
    }

    .recommendation-title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 15px;
        color: var(--text-dark);
        text-transform: capitalize;
    }

    .recommended-items {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 20px;
    }

    .recommended-item {
        background: var(--card-bg);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        border: 1px solid #eaeff5;
    }

    .item-header {
        padding: 15px;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .similarity-score {
        background: var(--success-color);
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
    }

    .item-previews {
        padding: 15px;
        display: flex;
        gap: 10px;
        overflow-x: auto;
    }

    .item-previews img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 8px;
        border: 1px solid var(--border-color);
    }

    .item-attributes {
        padding: 15px;
        background: #f8f9fa;
    }

    .processing-info {
        background: #e7f3ff;
        padding: 15px;
        border-radius: 8px;
        margin-top: 20px;
        border-left: 4px solid var(--primary-color);
    }

    .json-attribute {
        font-family: 'Courier New', monospace;
        font-size: 14px;
        background: #f8f9fa;
        padding: 10px;
        border-radius: 6px;
        border: 1px solid #e9ecef;
        max-height: 200px;
        overflow-y: auto;
        white-space: pre-wrap;
    }

    .image-preview {
        position: relative;
        max-height: 350px;
        max-width: 100%;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        margin: 0 auto 15px;
        aspect-ratio: 16/9;
    }

    .image-preview img {
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
        width: auto;
        height: auto;
        display: block;
    }

    .remove-preview {
        position: absolute;
        bottom: -15px;
        background: rgba(220, 53, 69, 0.8);
        color: white;
        border: none;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        cursor: pointer;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .loading,
    .error,
    .no-results,
    .warning {
        text-align: center;
        padding: 40px;
        font-size: 18px;
        grid-column: 1 / -1;
    }

    .error {
        background: #f8d7da;
        color: #721c24;
        border-radius: 8px;
        border: 1px solid #f5c6cb;
    }

    .warning {
        background: #fff3cd;
        color: #856404;
        border-radius: 8px;
        border: 1px solid #ffeaa7;
    }

    .spinner {
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top: 4px solid var(--primary-color);
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 992px) {
        .preview-area {
            grid-template-columns: 1fr;
        }

        .detected-furniture {
            grid-template-columns: 1fr;
        }

        .recommended-items {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 768px) {
        .controls {
            flex-direction: column;
        }

        .input-group {
            min-width: 100%;
        }

        .furniture-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }
    }

    @media (max-width: 480px) {
        .file-upload-container {
            padding: 15px;
        }
    }
</style>
